import {messages} from '@smartmixin/i18n';
import {Globe} from '@phosphor-icons/react';
import {MenuItem, Select, SelectChangeEvent} from '@mui/material';
import {useIntl} from 'react-intl';
import {useLocation, useNavigate} from 'react-router';
import {
  HELP_CONTENT_LOCALES,
  HELP_LOCALE_NAMES,
  helpPathForLocale,
  parseHelpLocaleFromPath,
} from '../locales';

/**
 * Deep-links between the translations of the current help page: switching
 * locale keeps the visitor on the same article/category, swapping only the URL
 * prefix (`/help/…` ↔ `/help/fr/…`). Query (`?platform=mobile`) and hash
 * deep links are preserved. Locale names are shown in their own language on
 * purpose — a visitor lost in the wrong language must be able to find theirs.
 */
const HelpLanguageSwitcher = () => {
  const navigate = useNavigate();
  const {pathname, search, hash} = useLocation();
  const {formatMessage} = useIntl();
  const current = parseHelpLocaleFromPath(pathname) ?? 'en';

  const handleChange = (event: SelectChangeEvent) => {
    const target = event.target.value;
    if (target !== current) {
      navigate(`${helpPathForLocale(pathname, target)}${search}${hash}`);
    }
  };

  return (
    <Select
      value={current}
      onChange={handleChange}
      size="small"
      variant="outlined"
      inputProps={{'aria-label': formatMessage(messages.settings.language)}}
      startAdornment={
        <Globe size={18} style={{marginRight: 6, flexShrink: 0}} />
      }
      sx={{
        fontSize: '0.875rem',
        color: 'text.secondary',
        '& .MuiOutlinedInput-notchedOutline': {borderColor: 'divider'},
      }}>
      {HELP_CONTENT_LOCALES.map((locale) => (
        <MenuItem key={locale} value={locale}>
          {HELP_LOCALE_NAMES[locale]}
        </MenuItem>
      ))}
    </Select>
  );
};

export default HelpLanguageSwitcher;
